import React, { useEffect, useState } from "react";
import { FileUploadService } from "../../../api";

interface FileMeta {
  collection_type: string;
  collection_ref: string;
  repo_type: string;
  fileName: string;
}

interface Props {
  fileMeta: FileMeta | null;
  open: boolean;
  onClose: () => void;
  title?: string;
}

const DocumentViewer: React.FC<Props> = ({
  fileMeta,
  open,
  onClose,
  title = "Document Viewer",
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileName = fileMeta?.fileName?.toLowerCase() || "";

  const isPdf = fileName.endsWith(".pdf");
  const isDoc = fileName.endsWith(".docx") || fileName.endsWith(".doc");
  const isText = fileName.endsWith(".txt");
  const isImage = fileType?.startsWith("image/");

  useEffect(() => {
    if (!open || !fileMeta || isDoc) return;

    let currentUrl: string | null = null;

    const fetchFile = async () => {
      try {
        setLoading(true);
        setError(null);
        setTextContent(null);

        const res =
          await FileUploadService.getFileDocrepoViewCollectionTypeCollectionRefRepoTypeFilenameGet(
            fileMeta.collection_type,
            fileMeta.collection_ref,
            fileMeta.repo_type,
            fileMeta.fileName
          );

        // console.log("RAW RESPONSE:", res);
        if (typeof res === "string") {
          // console.log("STRING RESPONSE:", res);
        }
        let blob: Blob;

        if (res instanceof Blob) {
          blob = res;
        }
        else if (res?.data instanceof Blob) {
          blob = res.data;
        }
        else if (typeof res === "string") {
          // ✅ THIS IS YOUR TXT CASE
          if (isText) {
            setTextContent(res);
            return;
          } else {
            blob = new Blob([res]);
          }
        }
        else if (typeof res?.blob === "function") {
          blob = await res.blob();
        }
        else {
          console.error("Unexpected response:", res);
          setError("Invalid file response");
          return;
        }

        if (isText) {
          const text = await blob.text();
          setTextContent(text);
        } else {
          currentUrl = URL.createObjectURL(blob);
          setBlobUrl(currentUrl);
          setFileType(blob.type);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load document");
      } finally {
        setLoading(false);
      }
    };

    fetchFile();

    return () => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      setTextContent(null);
    };
  }, [open, fileMeta, isDoc, isText]);

  const handleDownload = async () => {
    if (!fileMeta) return;

    const blob =
      await FileUploadService.getFileDocrepoViewCollectionTypeCollectionRefRepoTypeFilenameGet(
        fileMeta.collection_type,
        fileMeta.collection_ref,
        fileMeta.repo_type,
        fileMeta.fileName
      );

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileMeta.fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open || !fileMeta) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[90%] h-[90%] rounded flex flex-col">

        {/* Header */}
        <div className="flex justify-between p-3 border-b">
          <span>{title}</span>
          <button onClick={onClose}>Close</button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-3">

          {loading && <div>Loading...</div>}
          {error && <div className="text-red-500">{error}</div>}

          {/* PDF */}
          {!loading && !error && blobUrl && isPdf && (
            <iframe src={blobUrl} className="w-full h-full" />
          )}

          {/* Image */}
          {!loading && !error && blobUrl && isImage && (
            <img src={blobUrl} className="max-w-full max-h-full" />
          )}

          {/* TXT */}
          {!loading && !error && isText && textContent && (
            <pre className="text-xs whitespace-pre-wrap">
              {textContent}
            </pre>
          )}

          {/* DOC */}
          {isDoc && (
            <div className="text-center">
              <p>Preview not supported for docx/doc files. Please{" "}
                <button 
                  onClick={handleDownload}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Download
                </button> to view.
                </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;